import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { getProjections } from '../../../api/Projection/api';
import { showErrorToast } from '../../../utils/toastUtils';

const AmendmentModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  amendment, 
  mode = 'add' // Explicit modes: 'add', 'edit', 'review'
}) => {
  const [formData, setFormData] = useState({
    projection: '',
    projectedQuantity: '',
    proposedQuantity: '',
    reason: '',
    status: 'PENDING',
    reviewerNotes: ''
  });
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProjection, setSelectedProjection] = useState(null);

  useEffect(() => {
    if (open) {
      if (mode === 'add') fetchProjections();
      initializeFormData();
    }
  }, [open, amendment, mode]);

  const initializeFormData = () => {
    if (amendment) {
      setFormData({
        projection: amendment.projection || '',
        projectedQuantity: amendment.projectedQuantity || '',
        proposedQuantity: amendment.proposedQuantity || '',
        reason: amendment.reason || '',
        status: amendment.status || 'PENDING',
        reviewerNotes: amendment.reviewerNotes || ''
      });
      // Set selected projection for edit mode
      const projId = amendment.projection?.split('-')[1];
      const selected = projections.find(p => p.id == projId);
      setSelectedProjection(selected);
    } else {
      resetForm();
    }
  };

  const fetchProjections = async () => {
    try {
      setLoading(true);
      const response = await getProjections({"status": "DRAFT"});
      if (response?.data) {
        setProjections(response.data);
      }
    } catch (error) {
      console.error('Error fetching projections:', error);
      showErrorToast('Failed to fetch projections');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      projection: '',
      projectedQuantity: '',
      proposedQuantity: '',
      reason: '',
      status: 'PENDING',
      reviewerNotes: ''
    });
    setSelectedProjection(null);
  };

  const handleProjectionChange = (event) => {
    const selected = projections.find(proj => 
      `PROJ-${proj.id}-${proj.product_name}-${proj.duka_name}` === event.target.value
    );
    setSelectedProjection(selected);
    setFormData({
      ...formData,
      projection: event.target.value,
      projectedQuantity: selected?.forecasted_quantity || '',
      proposedQuantity: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Extract projection ID from projection string format: PROJ-{id}-...
    let projectionId = null;
    if (mode === 'add' && formData.projection) {
      const parts = formData.projection.split('-');
      if (parts.length >= 2) {
        projectionId = parts[1]; // The ID is the second part
      }
    }
    
    // Mode-specific validation
    switch(mode) {
      case 'add':
        if (!validateAddMode()) return;
        break;
      
      case 'edit':
        if (!validateEditMode()) return;
        break;
      
      case 'review':
        if (!validateReviewMode()) return;
        break;
    }

    const submissionData = prepareSubmissionData();
    onSubmit(submissionData, projectionId);
  };

  const validateAddMode = () => {
    if (!formData.projection) {
      showErrorToast('Please select a projection', 'Validation Error');
      return false;
    }
    return validateCommonFields();
  };

  const validateEditMode = () => {
    return validateCommonFields();
  };

  const validateReviewMode = () => {
    if (!formData.status) {
      showErrorToast('Please select a status', 'Validation Error');
      return false;
    }
    if (!formData.reviewerNotes) {
      showErrorToast('Please provide reviewer notes', 'Validation Error');
      return false;
    }
    return true;
  };

  const validateCommonFields = () => {
    const proposedQty = parseInt(formData.proposedQuantity);
    if (isNaN(proposedQty)) {
      showErrorToast('Proposed quantity must be a number', 'Validation Error');
      return false;
    }
    if (proposedQty <= 0) {
      showErrorToast('Proposed quantity must be greater than 0', 'Validation Error');
      return false;
    }
    if (!formData.reason) {
      showErrorToast('Please provide a reason', 'Validation Error');
      return false;
    }
    return true;
  };

  const prepareSubmissionData = () => {
    const baseData = {
      propose_quantity: parseInt(formData.proposedQuantity),
      reason: formData.reason,
      projection: formData.projection
    };

    switch(mode) {
      case 'review':
        return {
          status: formData.status,
          reviewer_notes: formData.reviewerNotes
        };
      
      case 'add':
        return {
          ...baseData,
          projection: formData.projection
        };
      
      default: // edit
        return baseData;
    }
  };

  const getModalConfig = () => {
    switch(mode) {
      case 'review':
        return {
          title: amendment?.status === 'PENDING' ? 'Review Amendment' : 'Amendment Details',
          submitText: amendment?.status === 'PENDING' ? 'Submit Review' : 'Close'
        };
      case 'edit':
        return {
          title: 'Edit Amendment',
          submitText: 'Save Changes'
        };
      case 'add':
      default:
        return {
          title: 'Create New Amendment',
          submitText: 'Submit Amendment'
        };
    }
  };

  const { title, submitText } = getModalConfig();
  const isReadOnly = mode === 'review' && amendment?.status !== 'PENDING';

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1 } }}
    >
      <DialogTitle sx={{
        p: 2,
        fontSize: 18,
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {title}
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Add/Edit Form Fields */}
            {(mode === 'add' || mode === 'edit') && (
              <>
                {mode === 'add' && (
                  <FormControl fullWidth required>
                    <InputLabel>Projection</InputLabel>
                    <Select
                      value={formData.projection}
                      onChange={handleProjectionChange}
                      label="Projection"
                      disabled={loading || mode !== 'add'}
                    >
                      {projections.map(proj => (
                        <MenuItem 
                          key={proj.id} 
                          value={`PROJ-${proj.id}-${proj.product_name}-${proj.duka_name}`}
                        >
                          {`PROJ-${proj.id}-${proj.product_name}-${proj.duka_name}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedProjection && (
                  <Typography variant="subtitle2" color="text.secondary">
                    Product: {selectedProjection.product_name}
                  </Typography>
                )}

                <TextField
                  label="Projected Quantity"
                  value={formData.projectedQuantity}
                  disabled
                  fullWidth
                />

                <TextField
                  label="Proposed Quantity"
                  type="number"
                  value={formData.proposedQuantity}
                  onChange={(e) => setFormData({ ...formData, proposedQuantity: e.target.value })}
                  required
                  fullWidth
                  disabled={mode === 'review'}
                  InputProps={{ inputProps: { min: 1 } }}
                />

                <TextField
                  label="Reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                  fullWidth
                  multiline
                  disabled={mode === 'review'}
                />
              </>
            )}

            {/* Review Form Fields */}
            {mode === 'review' && (
              <>
                <TextField
                  label="Projection"
                  value={formData.projection}
                  disabled
                  fullWidth
                />
                
                <TextField
                  label="Projected Quantity"
                  value={formData.projectedQuantity}
                  disabled
                  fullWidth
                />

                <TextField
                  label="Proposed Quantity"
                  value={formData.proposedQuantity}
                  disabled
                  fullWidth
                />

                <TextField
                  label="Reason"
                  value={formData.reason}
                  disabled
                  fullWidth
                  multiline
                />

                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status"
                    disabled={isReadOnly}
                    required={!isReadOnly}
                  >
                    <MenuItem value="APPROVED">Approve</MenuItem>
                    <MenuItem value="REJECTED">Reject</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Reviewer Notes"
                  value={formData.reviewerNotes}
                  onChange={(e) => setFormData({ ...formData, reviewerNotes: e.target.value })}
                  required={!isReadOnly}
                  fullWidth
                  multiline
                  disabled={isReadOnly}
                  helperText={!isReadOnly && "Please provide a reason for your decision"}
                />
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              bgcolor: '#e0e0e0',
              color: '#000',
              '&:hover': { bgcolor: '#d5d5d5' },
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          
          {(!isReadOnly || mode !== 'review') && (
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: '#00694B',
                color: '#fff',
                '&:hover': { bgcolor: '#005039' },
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                px: 3,
              }}
            >
              {submitText}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AmendmentModal;